<template>
  <div @mouseover="onHover" @mouseleave="onLeave">
    <ButtonSquareHoverCheckbox
      v-for="b in hoverButtons"
      v-show="(hoverButtons.length && showHoverButton) || activeState === 'active'"
      :key="b.id"
      :checkboxData="b"
      class="button-square-hover-checkbox"
      @toggle="onToggle(b.id, b.disabled)"
    />
    <div
      :aria-pressed="activeState === 'active'"
      :class="{
        'button-square--active': activeState === 'active',
        'button-square--dark': isDarkMode,
        'button-square--deactivated': activeState === 'deactivated',
        'button-square--large': size === 'large',
        'button-square--small': size === 'small',
        'button-square--text': hasTitle
      }"
      class="button-square"
      role="button"
      :tabindex="activeState === 'deactivated' ? -1 : 0"
      @click="onClick"
      @keydown.enter="onClick"
      @keydown.space="onClick"
      @mouseenter="$emit('mouseenter')"
      @mouseleave="$emit('mouseleave')"
    >
      <PulseLoader :loading="showLoadingSpinner" class="spinner" color="#949494" />
      <img
        v-if="!showLoadingSpinner"
        :src="imgSource"
        class="button-square__img"
        :class="{ 'button-square__img--text': hasTitle }"
        alt="Button icon"
      />
      <p v-if="hasTitle" class="button-square__p">
        {{ title }}
      </p>
      <slot />
    </div>
  </div>
</template>
<script setup lang="ts">
import { toRefs, ref, computed } from 'vue'
import ButtonSquareHoverCheckbox from './ButtonSquareHoverCheckbox.vue'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'

const props = withDefaults(
  defineProps<{
    activeState?: 'inactive' | 'active' | 'deactivated'
    activeHoverButtonValue?: string | number
    hoverButtonActiveState?: 'inactive' | 'active'
    hoverButtonTitle?: string
    hoverButtons?: any[]
    hoverExplanationText?: string
    imgSource?: string
    isDarkMode?: boolean
    isInMenu?: boolean
    showLoadingSpinner?: boolean
    size?: 'medium' | 'large' | 'small'
    title?: string
  }>(),
  {
    activeState: 'inactive',
    activeHoverButtonValue: '',
    hoverButtonActiveState: 'inactive',
    hoverButtonTitle: '',
    hoverButtons: () => [],
    hoverExplanationText: 'undefined',
    imgSource: '',
    isDarkMode: false,
    isInMenu: false,
    showLoadingSpinner: false,
    size: 'medium',
    title: ''
  }
)
const { activeState, size, title } = toRefs(props)
const emits = defineEmits<{
  (e: 'onChecklistToggle', id: string | number): void
  (e: 'clicked'): void
  (e: 'hovered'): void
  (e: 'mouseenter'): void
  (e: 'mouseleave'): void
}>()
const showHoverButton = ref(false)
const showHoverExplanationText = ref(false)
const hasTitle = computed(() => size.value === 'large' && title.value)
// function activeStateForHoverButton(button: any) {
//   //seems to not be used, do we need it?
//   activeHoverButtonValue.value === button.emit ? 'active' : 'inactive'
// }
function onClick() {
  emits('clicked')
}
function onHover() {
  if (activeState.value !== 'deactivated') {
    showHoverButton.value = true
    showHoverExplanationText.value = true
    emits('hovered')
  }
}
function onLeave() {
  showHoverButton.value = false
  showHoverExplanationText.value = false
  emits('mouseleave')
}
function onToggle(id: string | number, disabled: boolean) {
  if (disabled) return
  emits('onChecklistToggle', id)
}
</script>
<style lang="scss" scoped>
@use '@/styles/variables.scss';
@use '@/styles/mixins.scss';
.button-square {
  background-color: variables.$color-grey-lighter;
  border-radius: variables.$border-radius-medium;
  box-shadow: variables.$box-shadow-overview-button;
  display: inline-flex;
  height: variables.$size-button-medium;
  width: variables.$size-button-medium;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  flex-direction: column;
  padding: 8px;
  margin-right: 14px;
  &--active {
    background-color: variables.$color-grey-blue-dark;
    .button-square__img {
      filter: brightness(0) saturate(100%) invert(88%) sepia(0%) saturate(1%) hue-rotate(165deg)
        brightness(93%) contrast(95%); // The initial brightness/saturation is to turn the image black, before applying the rest of the filter
    }
    .button-square__p {
      color: variables.$color-text-white-70;
    }
  }
  &--dark {
    background-color: variables.$color-grey-blue-dark;
  }
  &--deactivated {
    cursor: auto;
    .button-square__img {
      filter: brightness(0) saturate(100%) invert(88%) sepia(0%) saturate(1%) hue-rotate(165deg)
        brightness(93%) contrast(95%); // The initial brightness/saturation is to turn the image black, before applying the rest of the filter
    }
    .button-square__p {
      color: variables.$color-grey;
    }
  }
  &--large {
    height: variables.$size-button-large;
    width: variables.$size-button-large;
    margin-right: 20px;
  }
  &--small {
    height: variables.$size-button-small;
    width: variables.$size-button-small;
    border-radius: variables.$border-radius-small;
    padding: 0;
    margin-right: 20px;
  }
  &--text {
    padding: 8px 8px 4px;
  }
  &__img {
    max-height: 100%;
    max-width: 100%;
    &--text {
      max-height: calc(100% - #{variables.$font-size-button-small});
    }
  }
  &__p {
    font-size: 9px;
    margin: variables.$padding-minor 0 0;
    line-height: 1;
    text-align: center;
  }
}
.hover-explanation {
  background-color: rgba(0, 0, 0, 0.5);
  bottom: 50px;
  box-shadow: variables.$box-shadow-overview-button;
  border-radius: 4px;
  padding: 2px 5px;
  display: inline-flex;
  justify-content: center;
  min-width: 96px;
  position: absolute;
  transform: translateX(-28%);
  &-menu {
    bottom: 64px;
  }
  &-text {
    color: variables.$color-text-white-70;
    flex-direction: column-reverse;
    margin: 0;
  }
}
.spinner {
  @include mixins.spinner;
  height: 30.6px;
}
@media screen and (max-width: 1024px) {
  .button-square {
    margin-right: 0px;

    &--large {
      margin: 2px 4px 0 4px;
    }
  }
  .button-square-hover-checkbox {
    flex-direction: row;
  }
}
@media screen and (max-width: 658px) {
  .button-square {
    margin-right: 0;
    &--large {
      margin-right: 0;
    }
  }
}
</style>
